import { ReactComponent as ArrowSvg } from 'assets/icons/arrow.svg';
import { useMeetInfo } from './utils/useMeetInfo';
import useMediaDevices from './utils/useMediaDevices';
import { PostCall } from './postCall/PostCall';
import { PreCall } from './preCall/PreCall';
import { useEffect, useState, useRef } from 'react';
import { InCall } from './inCall/inCall';
import Loading from './loading/Loading';
import { MediaDeviceInfoType, State } from './types/Video';
import OT from '@vonage/client-sdk-video';
import { VonageClient } from '@vonage/client-sdk';
import { createVonageSession } from './utils/createVonageSession';
import { createVideoSessionToken } from './utils/createVideoSessionToken';
import { getVonageToken } from './utils/getVonageToken';
import { saveInteractionAttributes } from './utils/saveInteractionAttributes';
import { useCallState } from './utils/useCallState';
import { logger } from 'utils/logger';

type SipStateProps = {
  accountId: number;
  widgetId: string;
  widgetName: string;
  url: string;
  visitorId: string;
  sessionId?: string | null;
  trueengage_form?: any;
  scheduleVideo?: boolean;
  // Vonage voice fields
  phone?: string;
  remoteName?: string;
  type?: string;
};

const Video = () => {
  const [currentState, setCurrentState] = useState<State>('preCall');
  // Session listeners outlive renders, so they read the state from here.
  const currentStateRef = useRef<State>(currentState);
  currentStateRef.current = currentState;
  const { meetInfo, fetchMeetInfoLoading, fetchMeetInfoError } = useMeetInfo();
  const isUpgrade = currentState === 'upgradeDashboard';

  // Vonage voice client (call signaling via @vonage/client-sdk)
  const [vonageVoiceClient, setVonageVoiceClient] = useState<VonageClient | null>(null);
  const vonageCallIdRef = useRef<string | null>(null);
  // Session listeners outlive renders, so they read the client from here.
  const vonageVoiceClientRef = useRef<VonageClient | null>(null);
  vonageVoiceClientRef.current = vonageVoiceClient;

  // Vonage video state
  const [vonageSession, setVonageSession] = useState<any>(null);
  const [vonagePublisher, setVonagePublisher] = useState<any>(null);
  const [vonageRemoteStream, setVonageRemoteStream] = useState<any>(null);
  const [vonageSessionId, setVonageSessionId] = useState<string | null>(null);
  const [vonageToken, setVonageToken] = useState<any>(null);
  const [vonageMicrophoneMuted, setVonageMicrophoneMuted] = useState<boolean>(false);
  const [vonageCameraMuted, setVonageCameraMuted] = useState<boolean>(false);
  const [vonageIsVideoBlurred, setVonageIsVideoBlurred] = useState<boolean>(false);
  // makeCall reads this after awaiting the SIP call, so it sees a mute clicked in the meantime.
  const vonageMicrophoneMutedRef = useRef(vonageMicrophoneMuted);
  vonageMicrophoneMutedRef.current = vonageMicrophoneMuted;
  const [frontCameraId, setFrontCameraId] = useState<string | null>(null);
  const [backCameraId, setBackCameraId] = useState<string | null>(null);
  const [cameraDevicesReady, setCameraDevicesReady] = useState<boolean>(false);
  const [isCameraRotated, setIsCameraRotated] = useState<boolean>(false);
  const [cameraPermissionDenied, setCameraPermissionDenied] = useState<boolean>(false);
  const localVideoRef = useRef<HTMLDivElement>(null);

  const vonageSessionRef = useRef<any>(null);
  const vonagePublisherRef = useRef<any>(null);
  useEffect(() => {
    vonageSessionRef.current = vonageSession;
  }, [vonageSession]);
  useEffect(() => {
    vonagePublisherRef.current = vonagePublisher;
  }, [vonagePublisher]);

  // Set by the agent's te_copy signal: audio then stays on the SIP leg and is never muted there.
  const teCopyRef = useRef(false);

  const setSipAudioAudible = (audible: boolean) => {
    if (teCopyRef.current) {
      console.log('[VIDEO][audio] setSipAudioAudible(' + audible + ') skipped, te_copy active');
      return;
    }
    try {
      const client: any = vonageVoiceClientRef.current;
      const callId = vonageCallIdRef.current;
      if (client && callId && typeof client.getPeerConnection === 'function') {
        const pc = client.getPeerConnection(callId);
        const receivers = pc?.getReceivers?.().filter((r: any) => r.track?.kind === 'audio') ?? [];
        receivers.forEach((r: any) => {
          r.track.enabled = audible;
        });
        console.log('[VIDEO][audio] setSipAudioAudible(' + audible + ') audio receivers=' + receivers.length);
      } else {
        console.warn('[VIDEO][audio] setSipAudioAudible(' + audible + ') no voice call', { client: !!client, callId });
      }
    } catch (e) {
      console.error('[VIDEO][audio] setSipAudioAudible error', e);
    }
  };

  const { callStatus, handleCallStateSignal, handleStreamCreated, handleStreamDestroyed } = useCallState({
    getPublisher: () => vonagePublisherRef.current,
    isCameraOn: () => !vonageCameraMuted,
    subscribe: (stream: any) => {
      console.log('[VIDEO][audio] subscribing to agent stream', { streamId: stream?.streamId, hasAudio: stream?.hasAudio, hasVideo: stream?.hasVideo });
      const subscriber = vonageSessionRef.current?.subscribe(stream, 'remoteVideoInCall', {
        insertMode: 'append',
        width: '100%',
        height: '100%',
        showControls: false,
      });
      subscriber?.on({
        audioBlocked: () => console.warn('[VIDEO][audio] agent audio blocked by browser autoplay policy'),
        audioUnblocked: () => console.log('[VIDEO][audio] agent audio unblocked'),
      });
      return subscriber;
    },
    unsubscribe: (sub: any) => {
      try {
        vonageSessionRef.current?.unsubscribe(sub);
      } catch {
        /* noop */
      }
    },
    setDisplayedRemoteStream: (s: any) => setVonageRemoteStream(s),
    setSipAudioAudible,
    endCall: (reason: string) => endCall(reason),
  });

  const handleTeCopySignal = (event: any) => {
    if (event?.data !== 'true') return;
    setSipAudioAudible(true);
    teCopyRef.current = true;
  };

  const blurSettings = {
    type: 'backgroundBlur' as const,
    blurStrength: 'high' as const,
  };

  // A new publisher starts from these options, so it keeps the camera and blur the visitor chose.
  const getPublisherOptions = (publishVideo: boolean = !vonageCameraMuted) => ({
    height: '100%',
    width: '100%',
    showControls: false,
    publishAudio: false, // The visitor's voice goes over the SIP call
    publishVideo,
    insertMode: 'append' as const,
    ...(vonageIsVideoBlurred && { videoFilter: blurSettings }),
  });

  const { availableDevices, selectedDevices, setSelectedDevices } = useMediaDevices();

  const updateDevice = async (device: MediaDeviceInfoType) => {
    try {
      if (device.kind === 'audiooutput') {
        await OT.setAudioOutputDevice(device.deviceId);
        setSelectedDevices((prev) => ({ ...prev, audioOutput: device as MediaDeviceInfo }));
      } else if (device.kind === 'audioinput') {
        await vonagePublisher?.setAudioSource(device.deviceId);
        setSelectedDevices((prev) => ({ ...prev, audioInput: device as MediaDeviceInfo }));
      } else if (device.kind === 'videoinput') {
        await vonagePublisher?.setVideoSource(device.deviceId);
        setSelectedDevices((prev) => ({ ...prev, videoInput: device as MediaDeviceInfo }));
      }
    } catch (error) {
      console.error('Failed to update device:', error);
    }
  };

  const changeCurrentState = (state: State) => {
    setCurrentState(state);
  };

  const getDeviceIdByFacingMode = async (mode: string) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode },
        audio: true,
      });
      const track = stream.getVideoTracks()[0];
      const settings = track.getSettings();
      const deviceId = settings.deviceId;

      stream.getTracks().forEach((t) => {
        t.stop();
      });

      setCameraPermissionDenied(false);
      return deviceId;
    } catch (error: any) {
      if (error?.name === 'NotAllowedError') {
        setCameraPermissionDenied(true);
      }
      console.error(`Error getting device ID for ${mode}:`, error);
      return null;
    }
  };

  const initializeCameraDeviceIds = async () => {
    try {
      const frontCamera = (await getDeviceIdByFacingMode('user')) ?? null;
      const backCamera = (await getDeviceIdByFacingMode('environment')) ?? frontCamera ?? null;
      setFrontCameraId(frontCamera);
      setBackCameraId(backCamera);
      setCameraDevicesReady(true);
    } catch (error) {
      console.error('Error initializing camera device IDs:', error);
      setCameraDevicesReady(true);
    }
  };

  const initVonagePreview = async (sessionId: string, token: any, elementId: string = 'localVideoPreCall') => {
    const applicationId = process.env.REACT_APP_VONAGE_APPLICATION_ID || '5ca20ba4-561c-42c5-b063-136f6ec614c6';
    console.log('Initializing Vonage preview for preCall:', sessionId);

    const session = OT.initSession(applicationId, sessionId);
    vonageSessionRef.current = session;
    session.on('signal:call-state', handleCallStateSignal);
    session.on('signal:te_copy', handleTeCopySignal);
    session.on('streamPropertyChanged', (event: any) => {
      if (event.changedProperty === 'hasAudio') {
        console.log('[VIDEO][audio] stream hasAudio changed', { streamId: event.stream?.streamId, hasAudio: event.newValue });
      }
    });

    session.on('streamCreated', (event: any) => {
      console.log('Stream created:', event);
      if (session.connection && event.stream.connection.connectionId !== session.connection.connectionId) {
        handleStreamCreated(event.stream);
      }
    });

    session.on('streamDestroyed', (event: any) => {
      console.log('Stream destroyed:', event);
      handleStreamDestroyed(event.stream);
    });

    session.on('sessionConnected', (event: any) => {
      console.log('Session connected (preCall preview):', event);
      // Don't publish yet, just show preview
    });

    session.on('sessionDisconnected', (event: any) => {
      console.log('Session disconnected:', event);
      if (currentStateRef.current === 'inCall') {
        endCall('video session disconnected (' + event?.reason + ')');
      }
    });

    const option = {
      height: '100%',
      width: '100%',
      showControls: false,
      publishAudio: false, // Don't publish audio in preview
      publishVideo: true, // Show video preview but don't publish to session yet
      insertMode: 'append' as const,
    };

    const publisher = OT.initPublisher(elementId, option, (error: any) => {
      if (error) {
        if (error.name === 'OT_USER_MEDIA_ACCESS_DENIED') {
          setCameraPermissionDenied(true);
        }
        console.error('Error initializing publisher for preview:', error);
      } else {
        setCameraPermissionDenied(false);
        console.log('Publisher initialized successfully for preview');
        console.log('Publisher element:', publisher.element);
      }
    });

    setVonageSession(session);
    setVonagePublisher(publisher);

    const tokenValue = token.token || token;
    console.log('Connecting with token for preview:', tokenValue);
    session.connect(tokenValue, (error: any) => {
      if (error) {
        console.error('Error connecting to session:', error);
      } else {
        console.log('Successfully connected to session for preview');
      }
    });
  };

  const initVonage = async (sessionId: string, token: any) => {
    // If publisher already exists from preCall, just publish it
    if (vonagePublisher && vonageSession) {
      console.log('Using existing publisher from preCall');
      vonageSession.publish(vonagePublisher, (error: any) => {
        if (error) {
          console.error('Error publishing existing publisher:', error);
        } else {
          console.log('Successfully published existing publisher');
        }
      });
      return;
    }

    // Otherwise initialize new session (fallback)
    const applicationId = process.env.REACT_APP_VONAGE_APPLICATION_ID || '5ca20ba4-561c-42c5-b063-136f6ec614c6';
    console.log('Initializing new Vonage session:', sessionId);

    const session = OT.initSession(applicationId, sessionId);
    vonageSessionRef.current = session;
    session.on('signal:call-state', handleCallStateSignal);
    session.on('signal:te_copy', handleTeCopySignal);
    session.on('streamPropertyChanged', (event: any) => {
      if (event.changedProperty === 'hasAudio') {
        console.log('[VIDEO][audio] stream hasAudio changed', { streamId: event.stream?.streamId, hasAudio: event.newValue });
      }
    });

    session.on('streamCreated', (event: any) => {
      console.log('Stream created:', event);
      if (session.connection && event.stream.connection.connectionId !== session.connection.connectionId) {
        handleStreamCreated(event.stream);
      }
    });

    session.on('streamDestroyed', (event: any) => {
      console.log('Stream destroyed:', event);
      handleStreamDestroyed(event.stream);
    });

    session.on('sessionConnected', (event: any) => {
      console.log('Session connected:', event);
      // Publikuj lokalny stream
      session.publish(publisher, (error: any) => {
        if (error) {
          console.error('Error publishing:', error);
        } else {
          console.log('Successfully published');
        }
      });
    });

    session.on('sessionDisconnected', (event: any) => {
      console.log('Session disconnected:', event);
      if (currentStateRef.current === 'inCall') {
        endCall('video session disconnected (' + event?.reason + ')');
      }
    });

    const publisher = OT.initPublisher('localVideoInCall', getPublisherOptions(), (error: any) => {
      if (error) {
        if (error.name === 'OT_USER_MEDIA_ACCESS_DENIED') {
          setCameraPermissionDenied(true);
        }
        console.error('Error initializing publisher:', error);
      } else {
        setCameraPermissionDenied(false);
        console.log('Publisher initialized successfully');
        console.log('Publisher element:', publisher.element);
      }
    });

    setVonageSession(session);
    setVonagePublisher(publisher);

    const tokenValue = token.token || token;
    console.log('Connecting with token:', tokenValue);
    session.connect(tokenValue, (error: any) => {
      if (error) {
        console.error('Error connecting to session:', error);
      } else {
        console.log('Successfully connected to session');
      }
    });
  };

  const vonageToggleMicrophone = () => {
    const callId = vonageCallIdRef.current;
    if (vonageMicrophoneMuted) {
      if (vonageVoiceClient && callId) vonageVoiceClient.unmute(callId);
      setVonageMicrophoneMuted(false);
    } else {
      if (vonageVoiceClient && callId) vonageVoiceClient.mute(callId);
      setVonageMicrophoneMuted(true);
    }
  };

  const sendDtmf = (digit: string) => {
    const callId = vonageCallIdRef.current;
    if (!vonageVoiceClient || !callId) {
      console.warn('[DTMF] no active voice call, digit not sent:', digit);
      return;
    }
    const sender = vonageVoiceClient
      .getPeerConnection(callId)
      ?.getSenders()
      .find((s) => s.track?.kind === 'audio');
    sender?.dtmf?.insertDTMF(digit);
  };

  const vonageToggleCamera = () => {
    if (vonagePublisher) {
      if (!vonageCameraMuted) {
        vonagePublisher.publishVideo(false);
        setVonageCameraMuted(true);
      } else if (currentState === 'preCall') {
        vonagePublisher.destroy();
        const element = document.getElementById('localVideoPreCall');
        if (element) element.innerHTML = '';
        const newPublisher = OT.initPublisher('localVideoPreCall', getPublisherOptions(true), (error: any) => {
          if (error) {
            if (error.name === 'OT_USER_MEDIA_ACCESS_DENIED') {
              setCameraPermissionDenied(true);
            }
            console.error('Error reinitializing camera preview:', error);
          } else {
            setCameraPermissionDenied(false);
          }
        });
        setVonagePublisher(newPublisher);
        setVonageCameraMuted(false);
      } else {
        vonagePublisher.publishVideo(true);
        setVonageCameraMuted(false);
      }
    }
  };

  const vonageToggleBlur = async () => {
    if (!vonagePublisher) return;
    try {
      if (vonageIsVideoBlurred) {
        await vonagePublisher.clearVideoFilter();
        setVonageIsVideoBlurred(false);
      } else {
        await vonagePublisher.applyVideoFilter(blurSettings);
        setVonageIsVideoBlurred(true);
      }
    } catch (error) {
      console.error('[blur] toggling background blur failed:', error);
    }
  };

  const switchingCameraRef = useRef(false);

  const vonageRotateCamera = async () => {
    if (!vonagePublisher || switchingCameraRef.current) return;
    const toBack = !isCameraRotated;
    const targetId = toBack ? backCameraId : frontCameraId;
    if (!targetId) {
      console.warn('[camera] no ' + (toBack ? 'back' : 'front') + ' camera id, not switching');
      return;
    }

    switchingCameraRef.current = true;
    try {
      await vonagePublisher.setVideoSource(targetId);
    } catch (error) {
      console.error('[camera] switching video source failed:', error);
      return;
    } finally {
      switchingCameraRef.current = false;
    }

    setIsCameraRotated(toBack);
    // Only touch the publisher's own video - the agent's video element comes first in the DOM.
    const videoElement = vonagePublisher.element?.querySelector('.OT_video-element') as HTMLElement | null;
    if (videoElement) {
      videoElement.style.transform = toBack ? 'scaleX(1)' : 'scaleX(-1)';
    }

    // iOS can pause other playing video elements while the camera is swapped.
    const remoteVideo = document.querySelector('#remoteVideoInCall video') as HTMLVideoElement | null;
    if (remoteVideo?.paused) {
      console.warn('[camera] agent video paused after camera switch, resuming');
      remoteVideo.play().catch((error) => console.error('[camera] resuming agent video failed:', error));
    }
  };

  const makeCall = async () => {
    if (meetInfo.meetId) {
      // Use existing Vonage sessionId from preCall if available
      let vonageSessionIdToUse = vonageSessionId;

      // If no session exists, create one (fallback)
      if (!vonageSessionIdToUse) {
        try {
          const participantData = {
            visitorId: meetInfo.visitorId,
            meetId: meetInfo.meetId,
            accountId: meetInfo.accountId,
          };

          const sessionResponse = await createVonageSession(meetInfo.accountId, participantData);
          vonageSessionIdToUse = sessionResponse.sessionId || sessionResponse.session_id;

          if (vonageSessionIdToUse) {
            setVonageSessionId(vonageSessionIdToUse);
            const tokenResponse = await createVideoSessionToken(vonageSessionIdToUse, meetInfo.accountId);
            setVonageToken(tokenResponse);
          }
        } catch (error) {
          logger.error('Failed to create Vonage session', String(error));
        }
      }

      let sipState: SipStateProps = {
        accountId: meetInfo.accountId,
        widgetId: meetInfo.additionalFields.widget_id,
        widgetName: meetInfo.additionalFields.widgetName,
        url: window.location.href,
        visitorId: meetInfo.visitorId,
        sessionId: vonageSessionIdToUse,
        scheduleVideo: true,
        phone: meetInfo.phoneNumber,
        remoteName: 'VideoApp',
        type: 'video',
      };

      if (meetInfo.additionalFields && meetInfo.additionalFields.trueengage_form) {
        sipState.trueengage_form = meetInfo.additionalFields.trueengage_form;
      }

      console.log('[makeCall] vonageVoiceClient:', vonageVoiceClient);
      console.log('[makeCall] sipState:', sipState);

      try {
        await saveInteractionAttributes(meetInfo.accountId, meetInfo.visitorId, sipState);
        console.log('[makeCall] saveInteractionAttributes done');
        console.log('[makeCall] sipState: ' + JSON.stringify(sipState));
        const callId = await vonageVoiceClient?.serverCall(sipState);
        console.log('[makeCall] serverCall callId:', callId);
        vonageCallIdRef.current = callId ?? null;
        // The visitor may have muted before joining, when there was no call to mute yet.
        if (callId && vonageMicrophoneMutedRef.current) {
          await vonageVoiceClient?.mute(callId);
          console.log('[makeCall] microphone muted before joining, muted the call');
        }
      } catch (err) {
        console.error('[makeCall] error:', err);
      }
    }
  };

  // Initialize Vonage session and publisher for preCall preview
  useEffect(() => {
    if (meetInfo.meetId && currentState === 'preCall' && cameraDevicesReady) {
      const initVonageForPreCall = async () => {
        try {
          const participantData = {
            visitorId: meetInfo.visitorId,
            meetId: meetInfo.meetId,
            accountId: meetInfo.accountId,
          };

          const sessionResponse = await createVonageSession(meetInfo.accountId, participantData);
          const sessionId = sessionResponse.sessionId || sessionResponse.session_id;

          if (sessionId) {
            setVonageSessionId(sessionId);
            const tokenResponse = await createVideoSessionToken(sessionId, meetInfo.accountId);
            setVonageToken(tokenResponse);

            // Wait for DOM element to be ready
            const checkElement = () => {
              const element = document.getElementById('localVideoPreCall');
              if (element) {
                initVonagePreview(sessionId, tokenResponse);
              } else {
                setTimeout(checkElement, 50);
              }
            };
            checkElement();
          }
        } catch (error) {
          logger.error('Failed to create Vonage session for preCall', String(error));
        }
      };

      initVonageForPreCall();
    }
  }, [meetInfo.meetId, currentState, cameraDevicesReady]);

  useEffect(() => {
    initializeCameraDeviceIds();
  }, []);

  // When entering inCall, recreate publisher for inCall element
  useEffect(() => {
    if (currentState === 'inCall' && vonageSession && vonagePublisher && vonageSessionId && vonageToken) {
      const inCallElement = document.getElementById('localVideoInCall');
      if (inCallElement) {
        // Destroy old publisher from preCall
        if (vonagePublisher.element) {
          vonagePublisher.destroy();
        }

        // Clear preCall element
        const preCallElement = document.getElementById('localVideoPreCall');
        if (preCallElement) {
          preCallElement.innerHTML = '';
        }

        // Create new publisher for inCall element
        setTimeout(() => {
          const newPublisher = OT.initPublisher('localVideoInCall', getPublisherOptions(), (error: any) => {
            if (error) {
              if (error.name === 'OT_USER_MEDIA_ACCESS_DENIED') {
                setCameraPermissionDenied(true);
              }
              console.error('Error initializing publisher for inCall:', error);
            } else {
              setCameraPermissionDenied(false);
              console.log('Publisher initialized successfully for inCall');
              setVonagePublisher(newPublisher);

              // Publish the new publisher
              vonageSession.publish(newPublisher, (error: any) => {
                if (error) {
                  console.error('Error publishing in inCall:', error);
                } else {
                  console.log('Successfully published in inCall');
                }
              });
            }
          });
        }, 100);
      } else {
        // Fallback: initialize new Vonage session
        initVonage(vonageSessionId, vonageToken);
      }
    }
  }, [currentState, vonageSession, vonageSessionId, vonageToken]);

  // Initialize Vonage voice client (call signaling)
  useEffect(() => {
    if (!meetInfo.accountId) return;

    getVonageToken(meetInfo.accountId, { meetId: meetInfo.meetId }, async (data: any) => {
      try {
        console.log('[Vonage voice] token response:', data);
        const client = new VonageClient({ region: 'EU' });
        await client.createSession(data.token);
        console.log('[Vonage voice] session created');

        client.on('callHangup', (callId: string) => {
          if (callId === vonageCallIdRef.current) {
            vonageCallIdRef.current = null;
            endCall('SIP call hung up');
          }
        });

        setVonageVoiceClient(client);
        console.log('[Vonage voice] client ready');
      } catch (error) {
        console.error('[Vonage voice] init failed:', error);
      }
    });
  }, [meetInfo.accountId, meetInfo.meetId]); // eslint-disable-line react-hooks/exhaustive-deps

  const callEndedRef = useRef(false);

  // Ends the SIP call and the video session together, whichever of them went first.
  const endCall = async (reason: string) => {
    if (callEndedRef.current) return;
    callEndedRef.current = true;
    console.log('[VIDEO] ending call: ' + reason);

    const client = vonageVoiceClientRef.current;
    const callId = vonageCallIdRef.current;
    vonageCallIdRef.current = null;
    if (client && callId) {
      try {
        await client.hangup(callId);
      } catch (error) {
        console.error('[VIDEO] SIP hangup failed:', error);
      }
    }
    const session = vonageSessionRef.current;
    if (session) {
      vonageSessionRef.current = null;
      session.disconnect();
      setVonageSession(null);
    }
    const publisher = vonagePublisherRef.current;
    if (publisher) {
      vonagePublisherRef.current = null;
      publisher.destroy();
      setVonagePublisher(null);
    }
    setCurrentState('postCall');
  };

  const handleDisconnect = () => endCall('visitor clicked end call');

  if (fetchMeetInfoLoading) return <Loading />;
  if (fetchMeetInfoError) return <div>{fetchMeetInfoError}</div>;
  if (currentState === 'preCall')
    return (
      <>
        <PreCall
          changeCurrentState={changeCurrentState}
          meetInfo={meetInfo}
          localVideoTrack={null}
          localBlurredVideoTrack={null}
          localAudioTrack={null}
          makeCall={makeCall}
          microphoneMuted={vonageMicrophoneMuted}
          cameraMuted={vonageCameraMuted}
          toggleMicrophone={vonageToggleMicrophone}
          toggleCamera={vonageToggleCamera}
          availableDevices={availableDevices}
          selectedDevices={selectedDevices}
          updateDevice={updateDevice}
          applyBlurOnPreCallVideo={vonageToggleBlur}
          isVideoBlurred={vonageIsVideoBlurred}
          isUpgrade={isUpgrade}
          useVonage={true}
          cameraPermissionDenied={cameraPermissionDenied}
        />
      </>
    );

  if (currentState === 'inCall')
    return (
      <>
        <InCall
          changeCurrentState={changeCurrentState}
          localVideoTrack={null}
          localAudioTrack={null}
          remoteVideoTrack={null}
          remoteAudioTrack={null}
          microphoneMuted={vonageMicrophoneMuted}
          cameraMuted={vonageCameraMuted}
          toggleMicrophone={vonageToggleMicrophone}
          toggleCamera={vonageToggleCamera}
          availableDevices={availableDevices}
          selectedDevices={selectedDevices}
          updateDevice={updateDevice}
          isVideoBlurred={vonageIsVideoBlurred}
          applyBlurOnInCallVideo={vonageToggleBlur}
          TurnCameraOnInCallVideo={vonageRotateCamera}
          isUpgrade={isUpgrade}
          handleDisconnect={handleDisconnect}
          vonageSession={vonageSession}
          vonagePublisher={vonagePublisher}
          vonageRemoteStream={vonageRemoteStream}
          sendDtmf={sendDtmf}
          callStatus={callStatus}
          cameraPermissionDenied={cameraPermissionDenied}
        />
      </>
    );

  if (currentState === 'postCall')
    return (
      <>
        <PostCall changeCurrentState={changeCurrentState} isVideoUpgrade={false} />
      </>
    );

  return null;
};

export default Video;
